import { Link } from "@remix-run/react";
import { useTranslation } from "react-i18next";

import qrCertificado from "~/assets/qr-certificado.png";
import { buildMeta, getCompanyFromMatches } from "~/utils/meta-helpers";

import { AcademiaSeal } from "../components/CuadernoVivo/AcademiaSeal";

import type { MetaFunction } from "@remix-run/node";

export const meta: MetaFunction = ({ matches }) => {
  const company = getCompanyFromMatches(matches);
  return buildMeta({
    title: `Ejemplo de certificado | ${company}`,
    description:
      "Así se ve un certificado de AcademIA. Es un modelo: cada credencial real se comprueba con su código.",
  });
};

/**
 * /certificado — el EJEMPLO. Es a donde apuntan la home y la ficha de curso con "mirá cómo
 * queda un certificado", y por eso sobrevive a la url con código.
 *
 * No dictamina nada, a propósito: antes decía "certificado verificado / es auténtico" con
 * datos de muestra, o sea que el sitio declaraba auténtico a un ejemplo. El veredicto vive
 * en /certificado/:code, que pregunta al servidor. Acá sólo se muestra el objeto.
 */
export default function CertificateExamplePage() {
  const { t } = useTranslation();

  return (
    <section className="landing-grain relative bg-landing-cream/70 px-5 py-16 backdrop-blur-[1px] sm:px-8 sm:py-20">
      <div className="relative mx-auto max-w-[640px]">
        <div className="text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-landing-border bg-landing-white px-4 py-2 text-[13px] font-bold text-landing-muted">
            Ejemplo · datos de muestra
          </span>
          <h1 className="font-display mt-4 text-balance text-[clamp(24px,3.4vw,34px)] font-extrabold leading-tight tracking-tight text-landing-text">
            Así queda un certificado de AcademIA
          </h1>
          <p className="mx-auto mt-2 max-w-[52ch] text-[14.5px] leading-relaxed text-landing-muted">
            El nombre, el curso y el código de abajo son inventados. En uno real, ese código abre
            una página con los datos de quien lo recibió, tal como los registramos.
          </p>
        </div>

        <div className="relative mx-auto mt-10 w-full rotate-[0.6deg] rounded-[6px] border-2 border-landing-border bg-landing-paper p-8 shadow-landing-xl sm:p-9">
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-[9px] rounded-[3px] border border-landing-red/35"
          />
          {/* Marca de agua: la página entera puede compartirse suelta, y una imagen de
              certificado sin contexto se confunde con una credencial de verdad. */}
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 flex items-center justify-center text-[clamp(48px,11vw,86px)] font-extrabold uppercase tracking-[0.18em] text-landing-text/[0.055] -rotate-[14deg]"
          >
            Ejemplo
          </span>
          <AcademiaSeal size={64} className="absolute -top-4 right-6 rotate-[8deg]" />
          <p className="font-display text-[16px] font-bold text-landing-text">
            Academ<span className="text-landing-red">IA</span>
          </p>
          <p className="mt-4 text-[10px] uppercase tracking-[0.2em] text-landing-muted">
            {t("landing.launch.cert.example.certifies")}
          </p>
          <p className="landing-hand mt-1 text-[27px] leading-none text-landing-text">
            {t("landing.launch.cert.example.name")}
          </p>
          <p className="mt-3 text-[10px] uppercase tracking-[0.2em] text-landing-muted">
            {t("landing.launch.cert.example.completed")}
          </p>
          <p className="font-display mt-1 text-[17px] text-landing-text">
            {t("landing.launch.cert.example.course")}
          </p>
          <div className="relative mt-6 flex items-end justify-between gap-4 border-t-[1.5px] border-dashed border-landing-border pt-4">
            <div className="grid gap-1.5 text-[12px] text-landing-muted [font-variant-numeric:tabular-nums]">
              <p>
                {t("landing.launch.cert.example.issued")}:{" "}
                <b className="text-landing-text">07 / 2026</b>
              </p>
              <p>
                {t("landing.launch.cert.example.token")}:{" "}
                <b className="text-landing-teal">#A1B2·C3D4·E5F6</b>
              </p>
              <p>{t("landing.launch.cert.example.verifyAt")}</p>
            </div>
            <img
              src={qrCertificado}
              alt="Código QR de verificación"
              width={64}
              height={64}
              className="h-16 w-16 shrink-0 rounded-[6px] border-[3px] border-white shadow-[0_0_0_1.5px_#E5DFD3]"
            />
          </div>
        </div>

        <p className="mx-auto mt-8 max-w-[52ch] text-center text-[13px] leading-relaxed text-landing-muted">
          Los cursos gratis entregan una constancia de finalización y los pagos, un certificado. Las
          dos llevan código propio y se comprueban de la misma forma.
        </p>

        <div className="mt-6 flex flex-wrap items-center justify-center gap-4 text-[14px]">
          <Link
            to="/verificar"
            data-cuelume-hover="tick"
            data-cuelume-press
            className="font-bold text-landing-teal underline decoration-landing-teal/40 underline-offset-4 hover:decoration-landing-teal"
          >
            Tengo un código: quiero verificarlo
          </Link>
          <span className="text-landing-border">·</span>
          <Link
            to="/#cursos"
            data-cuelume-hover="tick"
            data-cuelume-press
            className="font-bold text-landing-red underline decoration-landing-red/30 underline-offset-4 hover:decoration-landing-red"
          >
            Quiero el mío: empezá gratis
          </Link>
        </div>
      </div>
    </section>
  );
}
